import React, { useState } from 'react';
import { MAANEDER, AAR, formatNOK } from './data';

// Stiltokens — speiler C, card og mono i App.js
const C = {
  surface: '#FFFFFF', surface2: '#E8ECF0', border: '#CBD2DA',
  text: '#1A2332', muted: '#6B7888',
  green: '#2D7D9A', greenL: '#E3F2F8', red: '#C0392B', redL: '#FBEAE8',
  blue: '#2C5F8A', blueL: '#E3EDF6', amber: '#7D6608', amberL: '#FEF9E7',
  dark: '#1A2332',
};
const card = (extra = {}) => ({
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 10, padding: '18px 20px', ...extra
});
const mono = { fontFamily: "'DM Mono', monospace" };

// ══════════════════════════════════════════════════════════════════════════
// RE-KATEGORISERING AV KK-RÅDATA med dagens regler + læring, valgbart tidsrom.
// Forhåndsvisning først — skriving kun etter eksplisitt bekreftelse, og kun
// for måneder der rådataene forklarer faktisk-tallene (dekning OK).
// ══════════════════════════════════════════════════════════════════════════
export default function ReindekserKK({ token, lastFaktisk }) {
  const nå = new Date();
  const standardTom = nå.getFullYear() === AAR ? Math.max(1, nå.getMonth()) : 12; // forrige avsluttede måned
  const [aapen, setAapen] = useState(false);
  const [fra, setFra] = useState(1);
  const [tom, setTom] = useState(standardTom);
  const [preview, setPreview] = useState(null);
  const [laster, setLaster] = useState(false);
  const [skriver, setSkriver] = useState(false);
  const [melding, setMelding] = useState(null);
  const [analyse, setAnalyse] = useState(null);

  const hentAnalyse = async () => {
    setAnalyse({ laster: true });
    try {
      const r = await fetch('/api/admin/nokkel-analyse', { headers: { 'x-token': token } }).then(r => r.json());
      setAnalyse(r.ok ? r : { feil: r.error || 'Kunne ikke hente analyse' });
    } catch { setAnalyse({ feil: 'Nettverksfeil' }); }
  };

  const velgStil = { padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, background: C.surface, cursor: 'pointer' };

  const hentPreview = async () => {
    setLaster(true); setMelding(null); setPreview(null);
    try {
      const r = await fetch(`/api/admin/rekategoriser-kk-preview?aar=${AAR}&fra=${fra}&tom=${tom}`, { headers: { 'x-token': token } }).then(r => r.json());
      if (r.ok) setPreview(r);
      else setMelding({ type: 'feil', tekst: r.error || 'Kunne ikke hente forhåndsvisning' });
    } catch { setMelding({ type: 'feil', tekst: 'Nettverksfeil' }); }
    setLaster(false);
  };

  const okMaaneder = preview ? preview.maaneder.filter(m => m.dekning === 'ok') : [];
  const skrivbare = okMaaneder.filter(m => m.endringer.length > 0);

  const skriv = async () => {
    if (!window.confirm(`Skriv re-kategorisering for ${skrivbare.length} måned(er) (${skrivbare.reduce((s, m) => s + m.endringer.length, 0)} endringer) til budsjett.json? Måneder med avvik eller manglende rådata hoppes over.`)) return;
    setSkriver(true); setMelding(null);
    try {
      const r = await fetch('/api/admin/rekategoriser-kk-skriv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify({ aar: AAR, fraMaaned: fra, tomMaaned: tom }),
      }).then(r => r.json());
      if (r.ok) {
        setMelding({ type: 'ok', tekst: `Skrevet: ${r.antallOppdatert} transaksjoner re-kategorisert i ${r.skrevet.length} måned(er).${r.hoppetOver.length ? ` Hoppet over: ${r.hoppetOver.map(h => `${MAANEDER[h.maaned - 1]} (${h.grunn})`).join(', ')}` : ''}` });
        setPreview(null);
        await lastFaktisk();
      } else setMelding({ type: 'feil', tekst: r.error || 'Skriving feilet' });
    } catch { setMelding({ type: 'feil', tekst: 'Nettverksfeil ved skriving' }); }
    setSkriver(false);
  };

  const dekningBadge = (m) => {
    const stil = { padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600 };
    if (m.dekning === 'ok') return <span style={{ ...stil, background: C.greenL, color: C.green }}>Dekning OK</span>;
    if (m.dekning === 'ingen-raadata') return <span style={{ ...stil, background: C.surface2, color: C.muted }}>Ingen rådata — krever re-import</span>;
    return <span style={{ ...stil, background: C.redL, color: C.red }}>Avvik {formatNOK(m.avvik)} — krever re-import</span>;
  };

  return (
    <div style={{ ...card(), marginTop: 20, borderTop: `3px solid ${C.amber}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setAapen(v => !v)}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>🔁 Re-kategorisering av KK-data</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Kjør dagens kategoriregler og læring på rå KK-transaksjoner for valgt tidsrom</div>
        </div>
        <span style={{ fontSize: 18, color: C.muted }}>{aapen ? '▾' : '▸'}</span>
      </div>

      {aapen && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={fra} onChange={e => { const v = parseInt(e.target.value); setFra(v); if (v > tom) setTom(v); setPreview(null); }} style={velgStil}>
              {MAANEDER.map((m, i) => <option key={i} value={i + 1}>Fra: {m}</option>)}
            </select>
            <select value={tom} onChange={e => { const v = parseInt(e.target.value); setTom(v); if (v < fra) setFra(v); setPreview(null); }} style={velgStil}>
              {MAANEDER.map((m, i) => <option key={i} value={i + 1}>Til: {m}</option>)}
            </select>
            <button onClick={hentPreview} disabled={laster} style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {laster ? 'Henter…' : '🔍 Hent forhåndsvisning'}
            </button>
            <button onClick={hentAnalyse} style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>
              🔬 Nøkkelanalyse
            </button>
          </div>

          {/* Nøkkelanalyse: degenererte/kolliderende butikknøkler */}
          {analyse && (
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
              {analyse.laster && <span style={{ color: C.muted }}>Analyserer…</span>}
              {analyse.feil && <span style={{ color: C.red }}>{analyse.feil}</span>}
              {analyse.ok && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Nøkkelanalyse — {analyse.antallTransaksjoner} transaksjoner · {analyse.tommeNokler} med tom nøkkel · {analyse.korteNokler} med kort nøkkel (&lt; 4 tegn)
                  </div>
                  {analyse.grupperMedFlereNavn.length > 0 ? (
                    <>
                      <div style={{ color: C.muted, marginBottom: 4 }}>Nøkler som dekker flere ulike butikknavn (sjekk at de faktisk er samme butikk):</div>
                      {analyse.grupperMedFlereNavn.map(g => (
                        <div key={g.nokkel} style={{ padding: '4px 0', borderTop: `1px solid ${C.border}` }}>
                          <span style={{ ...mono, fontSize: 11, background: C.amberL, color: C.amber, padding: '1px 6px', borderRadius: 4 }}>«{g.nokkel}»</span>
                          <span style={{ color: C.muted }}> {g.antall} tx: </span>
                          {g.navn.join(' · ')}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{ color: C.green }}>✓ Ingen nøkler dekker flere ulike butikknavn — masse-matching er trygg.</div>
                  )}
                  <div style={{ color: C.muted, marginTop: 8 }}>
                    Største grupper: {analyse.stoersteGrupper.map(g => `«${g.nokkel}» (${g.antall})`).join(' · ')}
                  </div>
                </>
              )}
            </div>
          )}

          {melding && (
            <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12, background: melding.type === 'ok' ? C.greenL : C.redL, color: melding.type === 'ok' ? C.green : C.red, borderLeft: `3px solid ${melding.type === 'ok' ? C.green : C.red}` }}>
              {melding.tekst}
            </div>
          )}

          {preview && (
            <div>
              {/* Statuslinje — vises alltid når forhåndsvisning er hentet */}
              <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12, background: okMaaneder.length > 0 ? C.greenL : C.amberL, color: okMaaneder.length > 0 ? C.green : C.amber, borderLeft: `3px solid ${okMaaneder.length > 0 ? C.green : C.amber}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span>
                  <strong>{okMaaneder.length} av {preview.maaneder.length} måneder kan skrives</strong> ({preview.totaltEndringer} endringer totalt, {skrivbare.reduce((s, m) => s + m.endringer.length, 0)} i skrivbare måneder).
                  {okMaaneder.length < preview.maaneder.length && ' Måneder med avvik eller manglende rådata krever re-import av DNB Excel først.'}
                </span>
                {skrivbare.length > 0 && (
                  <button onClick={skriv} disabled={skriver} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                    {skriver ? 'Skriver…' : `✓ Skriv ${skrivbare.length} måned(er)`}
                  </button>
                )}
              </div>

              {preview.maaneder.map(m => (
                <div key={m.maaned} style={{ marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {MAANEDER[m.maaned - 1]} {preview.aar}
                      <span style={{ ...mono, fontWeight: 400, color: C.muted, marginLeft: 8, fontSize: 11 }}>
                        {m.antall} rå-tx · rådata {formatNOK(m.sumPush)} / faktisk KK {formatNOK(m.sumFaktiskKK)}
                      </span>
                    </div>
                    {dekningBadge(m)}
                  </div>

                  {m.endringer.length > 0 ? (
                    <>
                      {/* Kategorisummer før → etter */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
                        {m.kategoriFørEtter.map(r => (
                          <span key={r.kategori} style={{ ...mono, fontSize: 10, background: C.blueL, color: C.blue, padding: '3px 8px', borderRadius: 6 }}>
                            {r.kategori}: {formatNOK(r.foer)} → {formatNOK(r.etter)}
                          </span>
                        ))}
                      </div>
                      {/* Transaksjonene som flyttes */}
                      <div className="scroll-x" style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <div style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '70px 1.2fr 80px 1fr 1fr', fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                          <div>Dato</div><div>Butikk</div><div style={{ textAlign: 'right' }}>Beløp</div><div>Fra</div><div>Til</div>
                        </div>
                        {m.endringer.map(e => (
                          <div key={e.id} style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '70px 1.2fr 80px 1fr 1fr', fontSize: 11, padding: '4px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
                            <div style={{ color: C.muted }}>{(e.dato || '').slice(5)}</div>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.butikk}</div>
                            <div style={{ ...mono, textAlign: 'right' }}>{formatNOK(e.beloep)}</div>
                            <div style={{ color: C.red }}>{e.fra}</div>
                            <div style={{ color: C.green, fontWeight: 600 }}>{e.til}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                      {m.dekning === 'ingen-raadata' ? 'Ingen rå KK-transaksjoner lagret for denne måneden.' : 'Ingen kategoriendringer med dagens regler.'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
